/**
 * ─── Motion Constants ──────────────────────────────────────────────────────
 *
 * Site-wide GSAP duration and easing constants.
 * Mirrors the CSS duration/easing tokens in src/index.css.
 *
 * Usage:
 *   import { DUR, EASE } from '../../lib/motion';
 *   gsap.to(el, { duration: DUR.motion, ease: EASE.out, opacity: 1 });
 *
 * ── IMPORTANT EXCLUSION ────────────────────────────────────────────────────
 * These constants do NOT apply to canvas / sketch timing.
 * The following are GPU-frame-rate-dependent and must be managed
 * independently within each Sketch class:
 *   - src/containers/Home/Sketch/Sketch.js
 *   - src/components/Work/MotionPicture/Sketch.js
 *   - src/components/Demos/LabChamber/LabChamber.js (gsap.set layout, slot tilt)
 * ──────────────────────────────────────────────────────────────────────────
 */

/**
 * Duration tiers (seconds).
 *
 * micro   Hover in/out, opacity flicker, small label fades
 * motion  Element entries, state transitions, dot/filter changes
 * flow    Page enters, section content arrivals, large reveals
 */
export const DUR = {
  micro:  0.20,
  motion: 0.35,
  flow:   0.60,
};

/**
 * GSAP easing identifiers.
 *
 * out     Default UI ease-out — most element transitions and state changes
 * in      Exit / dismiss — element leaving the screen or fading out
 * inOut   Symmetric — section content crossfades, background opacity
 * strong  High-inertia — section scroll navigation (locked to site personality)
 * snap    Overshoot / elastic — carousel snap (LabChamber virtualOffset)
 *
 * ── Resolved (post-stabilization) ──────────────────────────────────────────
 * Home.js section scroll ease: all interactive paths (wheel, touch, menu)
 * now route through goToSection(), which uses EASE.strong (power4.inOut).
 * The separate wheel-handler no longer carries an overriding ease.
 * EASE.strong is the canonical section-navigation ease.
 *
 * ── Partially resolved (post-stabilization) ─────────────────────────────────
 * Home.js section exit patterns:
 *   Mount:              opacity: 0.0  (intentional — sections start invisible)
 *   Wheel/touch/menu:   opacity: 0.5  via goToSection()  ← unified
 * Only componentDidMount differs by design; all live navigation is consistent.
 */
export const EASE = {
  out:    'power2.out',
  in:     'power2.in',
  inOut:  'power2.inOut',
  strong: 'power4.inOut',
  snap:   'expo.out',
};

/**
 * Motion roles (documentation — not executable).
 *
 * ┌─────────────────────┬──────────────────┬──────────────┬────────────────────────────────────┐
 * │ Role                │ Duration         │ Ease         │ Examples                           │
 * ├─────────────────────┼──────────────────┼──────────────┼────────────────────────────────────┤
 * │ Section transition  │ 1.0s (locked)    │ EASE.strong  │ Home scroll between sections       │
 * │ Element entry       │ DUR.flow  0.6s   │ EASE.out     │ Section content stagger-in         │
 * │ State change        │ DUR.motion 0.35s │ EASE.out     │ Filter active, dot active, meta    │
 * │ Hover micro         │ DUR.micro  0.2s  │ EASE.out     │ Button/arrow/link hover            │
 * │ Drag response       │ instantaneous    │ gsap.set     │ LabChamber slot layout (excluded)  │
 * │ Exit / dismiss      │ micro–motion     │ EASE.in      │ Label fade-out, hint dim           │
 * └─────────────────────┴──────────────────┴──────────────┴────────────────────────────────────┘
 *
 * Drag hint lifecycle rule:
 *   Show on section entry. Dim (do not disappear) on first interaction
 *   — reduce to --ui-opacity-ghost (0.25). Never auto-fade on a timer
 *   independent of user interaction. Persist as a minimal ghost reminder.
 *   (Application: Phase 2+)
 *
 * Section anchor principle:
 *   Every section must have one visually resolved dominant anchor on arrival
 *   — legible at first frame without hover or interaction.
 *   (Application: Phase 2+)
 *
 * Active state rule:
 *   Active states must change at least two distinct property types.
 *   Opacity + font-weight counts as one type (both text quality).
 *   Add a spatial or structural property: scale, translate, underline, shape.
 *   (Phase 2 flag: WorkFilters + LabFilters FilterButtonActive nonconformant)
 */

/**
 * Returns true when the user has requested reduced motion at the OS level.
 * Call this inside each animation helper/trigger and simplify accordingly:
 *   - collapse large y offsets to 0
 *   - use an effectively-instant duration (e.g. 0.01)
 *   - skip scale transforms
 *
 * Usage:
 *   import { prefersReducedMotion } from '../../lib/motion';
 *   const reduced = prefersReducedMotion();
 *   gsap.fromTo(el,
 *     { y: reduced ? 0 : 40, opacity: 0 },
 *     { y: 0, opacity: 1, duration: reduced ? 0.01 : DUR.flow, ease: EASE.out }
 *   );
 */
export function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
